import { Button, Grid, TextField, MenuItem, DialogActions,IconButton } from '@mui/material';
import React, { useState, useContext } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import AccountServices from 'services/AccountServices';

export default function AccountHeadDialog({ onSuccess, onClose, accountHead, existingAccounts = [] }) {
  const { t: tCommon } = useTranslation('common');

  const GROUPS = ['ASSET', 'LIABILITY', 'INCOME', 'EXPENSE', 'EQUITY'];
  const TYPES = ['PAYMENT_MODE', 'CUSTOMER', 'SUPPLIER', 'GENERAL'];

  const formatGroup = (g) => {
    const formatted = g.toLowerCase();
    const capitalized = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return tCommon(formatted, capitalized);
  };

  const formatType = (t) => {
    if (t === 'PAYMENT_MODE') return tCommon('payment_mode');
    if (t === 'CUSTOMER') return tCommon('customer');
    
    const formatted = t.replace(/_/g, ' ').toLowerCase();
    const capitalized = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return tCommon(t.toLowerCase(), capitalized);
  };

  const isEditMode = !!accountHead;

  const [name, setName] = useState(isEditMode ? accountHead.name : '');
  const [groupName, setGroupName] = useState(isEditMode ? accountHead.group_name : 'ASSET');
  const [type, setType] = useState(isEditMode ? accountHead.type : 'GENERAL');
  const [balance, setBalance] = useState(isEditMode ? accountHead.balance : '');
  const [order, setOrder] = useState(isEditMode ? (accountHead.order || '') : '');
  const [isEnabled, setIsEnabled] = useState(isEditMode ? accountHead.is_enabled : true);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const handleSubmit = async () => {
    const trimmedName = name.trim().toLowerCase();
    const isDuplicate = existingAccounts.some((account) => {
      if (isEditMode && String(account.id) === String(accountHead.id)) {
        return false;
      }
      return account.name.trim().toLowerCase() === trimmedName && account.group_name === groupName;
    });

    // Only enforce duplicate validation if creating new or if the name/group changed
    const isNameOrGroupChanged = isEditMode
      ? (name.trim().toLowerCase() !== (accountHead.name || '').trim().toLowerCase() || groupName !== accountHead.group_name)
      : true;

    if (isNameOrGroupChanged && isDuplicate) {
      postAlertMessage({
        text: tCommon('Account Head name already exists in this group'),
        type: 'failed',
      });
      return;
    }

    const data = {
      name,
      group_name: groupName,
      type,
      balance: Number(balance) || 0,
      order: order !== '' ? Number(order) : null,
      is_enabled: isEnabled,
    };
    
    setLoadingIndicator(true);
    try {
      if (isEditMode) {
        await AccountServices.updateAccountHead(accountHead.id, data);
        postAlertMessage({
          text: tCommon('Account Head updated successfully'),
          type: 'success',
        });
      } else {
        await AccountServices.createAccountHead(data);
        postAlertMessage({
          text: tCommon('Account Head created successfully'),
          type: 'success',
        });
      }
      onSuccess();
    } catch (err) {
      postAlertMessage({
        text: err.message,
        type: 'failed',
      });
    }
    setLoadingIndicator(false);
  };

  return (
    <div className={styles.popUp} style={{ zIndex: 1300 }}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>
            {isEditMode ? tCommon('Edit Account Head') : tCommon('Create Account Head')}
          </h2>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="top" spacing={2} style={{ justifyContent: 'flex-start' }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Name')}
                variant="outlined"
                size="small"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Opening Balance')}
                className={styles.numberInput}
                name="balance"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Group Name')}
                variant="outlined"
                size="small"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className={styles.selectBox}
                fullWidth
                SelectProps={{
                  MenuProps: {
                    style: { zIndex: 999999999 },
                  },
                }}
              >
                {GROUPS.map((group) => (
                  <MenuItem key={group} value={group}>
                    {formatGroup(group)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Type')}
                variant="outlined"
                size="small"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={styles.selectBox}
                fullWidth
                SelectProps={{
                  MenuProps: {
                    style: { zIndex: 999999999 },
                  },
                }}
              >
                {TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {formatType(t)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Display Order')}
                className={styles.numberInput}
                name="order"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
              />
            </Grid>
            {isEditMode && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={tCommon('Status')}
                  variant="outlined"
                  size="small"
                  value={isEnabled ? 'active' : 'inactive'}
                  onChange={(e) => setIsEnabled(e.target.value === 'active')}
                  className={styles.selectBox}
                  fullWidth
                  SelectProps={{
                    MenuProps: {
                      style: { zIndex: 999999999 },
                    },
                  }}
                >
                  <MenuItem value="active">{tCommon('Active')}</MenuItem>
                  <MenuItem value="inactive">{tCommon('Inactive')}</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={onClose}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={name === '' || loadingIndicator}
              onClick={handleSubmit}
            >
              {isEditMode ? tCommon('Update') : tCommon('Create Account Head')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
